import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Container from '../common/Container';
import Card from '../common/Card';
import Button from '../common/Button';
import Text from '../common/Text';
import { assessmentService, Status, AssessmentResponse, AssessmentType } from '../../api/services/assessment';
import AssessmentTypeCard from './AssessmentTypeCard';
import AssessmentResults from './AssessmentResults';

const SkillDiagnostic: React.FC = () => {
  const { tenant } = useParams<{ tenant: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [latestAssessment, setLatestAssessment] = useState<AssessmentResponse | null>(null);
  const hasFetched = useRef(false);
  const isFetching = useRef(false);

  useEffect(() => {
    const fetchLatestAssessment = async () => {
      if (isFetching.current || hasFetched.current) return;

      try {
        isFetching.current = true;
        const assessment = await assessmentService.getLatestAssessment();
        setLatestAssessment(assessment);
        hasFetched.current = true;
      } catch (err) {
        console.error('Error fetching latest assessment:', err);
        setError('Failed to load assessment data');
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    };

    fetchLatestAssessment();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-blue-500/20 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-blue-500 rounded-full border-t-transparent animate-spin absolute top-0"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100/50 dark:border-gray-700/50 p-6 text-center">
            <Text variant="h2" className="text-red-600 mb-4">Error</Text>
            <Text variant="p" className="mb-6">{error}</Text>
            <Button
              onClick={() => navigate(`/${tenant}/dashboard`)}
              variant="secondary"
              size="md"
              className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 text-white shadow-lg shadow-gray-500/25 hover:shadow-xl hover:shadow-gray-500/30 transition-all duration-300"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  // If there's a completed assessment, show the summary
  if (latestAssessment?.status === Status.COMPLETED && latestAssessment?.results) {
    return (
      <Container>
        <AssessmentResults
          score={latestAssessment.results.score}
          correctAnswers={latestAssessment.results.correct_answers}
          totalQuestions={latestAssessment.results.total_questions}
          timePerQuestion={latestAssessment.results.time_per_question}
          recommendations={latestAssessment.results.recommendations}
          questions={latestAssessment.questions}
          answers={latestAssessment.answers?.reduce((acc: Record<string, string>, answer: any) => ({
            ...acc,
            [answer.content_id]: answer.user_answer
          }), {}) || {}}
        />
      </Container>
    );
  }

  // Show assessment type selection if no completed assessment or assessment is in progress
  return (
    <Container>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 bg-clip-text text-transparent">
            Skill Diagnostic
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Choose an assessment type to evaluate your knowledge and skills. Get personalized recommendations based on your performance.
          </p>
        </div>

        <div className="grid gap-12 md:grid-cols-2">
          <AssessmentTypeCard
            type={AssessmentType.QUICK}
            title="Quick Assessment"
            duration="5-10 minutes"
            description="A brief assessment to quickly evaluate your current knowledge level."
            features={[
              "10 multiple-choice questions",
              "Instant results",
              "Basic recommendations"
            ]}
            color="blue"
            onStart={() => navigate(`/${tenant}/skill-diagnostic/quick`)}
            isStarting={false}
          />

          <AssessmentTypeCard
            type={AssessmentType.COMPREHENSIVE}
            title="Comprehensive Assessment"
            duration="20-30 minutes"
            description="A detailed assessment covering various topics and question types."
            features={[
              "30 diverse questions",
              "Detailed analysis",
              "Personalized learning path"
            ]}
            color="green"
            isComingSoon={true}
            onStart={() => navigate(`/${tenant}/skill-diagnostic/start/comprehensive`)}
            isStarting={false}
          />
        </div>
      </div>
    </Container>
  );
};

export default SkillDiagnostic;